import React, { useState, useEffect } from 'react';
import { X, Fingerprint, AlertCircle, Loader, CheckCircle } from 'lucide-react';
import { useQuery, useMutation } from '@tanstack/react-query';
import { useWebSocket } from '../contexts/WebSocketContext';
import fingerprintApi, { FingerprintDevice } from '../services/fingerprintApi';
import shiftApi, { Shift } from '../services/shiftApi';
import toast from 'react-hot-toast';

interface AttendanceLogoffModalProps {
  shift: Shift;
  onClose: () => void;
  onLogoffSuccess: () => void;
  canMarkAttendanceForOthers: boolean;
}

const AttendanceLogoffModal: React.FC<AttendanceLogoffModalProps> = ({
  shift,
  onClose,
  onLogoffSuccess,
  canMarkAttendanceForOthers
}) => {
  const { isConnected } = useWebSocket();
  const [step, setStep] = useState<'verify' | 'verifying' | 'success' | 'error'>('verify');
  const [selectedDeviceId, setSelectedDeviceId] = useState<number | null>(null);
  const [useAdminOverride, setUseAdminOverride] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [statusMessage, setStatusMessage] = useState<string>('');

  // Get available devices
  const { data: devicesData } = useQuery({
    queryKey: ['fingerprint-devices-my'],
    queryFn: () => fingerprintApi.getMyFingerprintDevices(),
    enabled: step === 'verify' || step === 'verifying'
  });

  const devices: FingerprintDevice[] = devicesData?.devices || [];

  // Auto-select first device when devices are loaded
  useEffect(() => {
    if (devices.length > 0 && !selectedDeviceId) {
      const firstActiveDevice = devices.find(d => d.status === 'active') || devices[0];
      if (firstActiveDevice) {
        setSelectedDeviceId(firstActiveDevice.deviceId);
      }
    }
  }, [devices, selectedDeviceId]);

  // Fetch employee fingerprint data
  const { data: fingerprintData, isLoading: loadingFingerprint } = useQuery({
    queryKey: ['fingerprint-template', shift.employeeId],
    queryFn: () => fingerprintApi.getFingerprintTemplate(shift.employeeId),
    enabled: !useAdminOverride && (step === 'verify' || step === 'verifying')
  });

  // Logoff mutation
  const logoffMutation = useMutation({
    mutationFn: (data: {
      fingerprintTemplate?: string;
      deviceId?: number;
      adminOverride?: boolean;
    }) => shiftApi.logoffWithFingerprint(shift.id, data),
    onSuccess: () => {
      setStep('success');
      toast.success('Logoff successful');
      setTimeout(() => {
        onLogoffSuccess();
      }, 2000);
    },
    onError: (error: any) => {
      setStep('error');
      setErrorMessage(error.response?.data?.message || 'Failed to logoff');
      toast.error(error.response?.data?.message || 'Failed to logoff');
    }
  });

  const handleLogoff = async () => {
    if (useAdminOverride) {
      if (!canMarkAttendanceForOthers) {
        toast.error('Admin override requires admin permissions');
        return;
      }

      setStep('verifying');
      setErrorMessage('');
      setStatusMessage('Processing logoff with admin override...');

      logoffMutation.mutate({
        adminOverride: true
      });
      return;
    }

    // Fingerprint verification required
    if (!selectedDeviceId) {
      toast.error('Please select a fingerprint device');
      return;
    }

    if (!isConnected) {
      toast.error('WebSocket not connected. Please wait...');
      return;
    }

    if (!fingerprintData?.employee?.fingerprintTemplate) {
      toast.error('Employee does not have fingerprint enrolled');
      return;
    }

    const fingerprintTemplate = fingerprintData.employee.fingerprintTemplate;

    try {
      setStep('verifying');
      setErrorMessage('');
      setStatusMessage('Verifying fingerprint...');

      // Verify fingerprint first
      await fingerprintApi.verifyFingerprint({
        deviceId: selectedDeviceId,
        fingerprintTemplate: fingerprintTemplate
      });

      setStatusMessage('Fingerprint verified. Processing logoff...');

      // Logoff
      logoffMutation.mutate({
        fingerprintTemplate: fingerprintTemplate,
        deviceId: selectedDeviceId
      });
    } catch (error: any) {
      // If verification fails and user has admin access, offer override
      if (canMarkAttendanceForOthers) {
        setStep('error');
        setErrorMessage('Fingerprint verification failed. You can use admin override.');
        toast.error('Fingerprint verification failed. You can use admin override.');
      } else {
        setStep('error');
        setErrorMessage(error.response?.data?.message || 'Fingerprint verification failed');
        toast.error(error.response?.data?.message || 'Fingerprint verification failed');
      }
    }
  };

  const hasFingerprint = fingerprintData?.employee?.hasFingerprint;

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-20 mx-auto p-5 border w-full max-w-md shadow-lg rounded-md bg-white">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-medium text-gray-900">Logoff Employee</h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
            disabled={step === 'verifying'}
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        <div className="mb-4">
          <p className="text-sm text-gray-600">
            Employee: <span className="font-medium">{shift.employee.user.name}</span>
          </p>
          <p className="text-sm text-gray-600">
            Started: {new Date(shift.startedAt).toLocaleString()}
          </p>
        </div>

        {step === 'verify' && (
          <div className="space-y-4">
            {/* Admin Override Option */}
            {canMarkAttendanceForOthers && (
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="adminOverride"
                  checked={useAdminOverride}
                  onChange={(e) => setUseAdminOverride(e.target.checked)}
                  className="rounded"
                />
                <label htmlFor="adminOverride" className="text-sm text-gray-700">
                  Use admin override (skip fingerprint verification)
                </label>
              </div>
            )}

            {!useAdminOverride && (
              <>
                {/* Fingerprint Status */}
                {loadingFingerprint ? (
                  <div className="flex items-center gap-2 text-sm text-gray-600">
                    <Loader className="w-4 h-4 animate-spin" />
                    Checking fingerprint status...
                  </div>
                ) : hasFingerprint ? (
                  <div className="flex items-center gap-2 text-sm text-green-600">
                    <Fingerprint className="w-4 h-4" />
                    Fingerprint enrolled
                  </div>
                ) : (
                  <div className="flex items-center gap-2 text-sm text-red-600">
                    <AlertCircle className="w-4 h-4" />
                    Fingerprint not enrolled
                  </div>
                )}

                {/* Device Selection */}
                {hasFingerprint && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Fingerprint Device
                    </label>
                    <select
                      value={selectedDeviceId || ''}
                      onChange={(e) => setSelectedDeviceId(e.target.value ? parseInt(e.target.value) : null)}
                      className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    >
                      <option value="">-- Select Device --</option>
                      {devices.map((device) => (
                        <option key={device.id} value={device.deviceId}>
                          {device.name} (ID: {device.deviceId}) {device.status === 'active' ? '🟢' : '🔴'}
                        </option>
                      ))}
                    </select>
                    {devices.length === 0 && (
                      <p className="mt-1 text-xs text-gray-500">No devices available.</p>
                    )}
                    {!isConnected && (
                      <p className="mt-2 text-xs text-yellow-600">WebSocket not connected. Please wait...</p>
                    )}
                  </div>
                )}
              </>
            )}

            <div className="flex justify-end gap-4 pt-4 border-t">
              <button
                onClick={onClose}
                className="px-4 py-2 bg-gray-300 text-gray-800 rounded hover:bg-gray-400"
              >
                Cancel
              </button>
              <button
                onClick={handleLogoff}
                disabled={
                  (!useAdminOverride && (!hasFingerprint || !selectedDeviceId || !isConnected)) ||
                  logoffMutation.isPending
                }
                className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 disabled:bg-gray-300 disabled:cursor-not-allowed"
              >
                {useAdminOverride ? 'Logoff (Admin Override)' : 'Verify & Logoff'}
              </button>
            </div>
          </div>
        )}

        {step === 'verifying' && (
          <div className="text-center py-8">
            <Loader className="w-16 h-16 mx-auto text-primary-600 mb-4 animate-spin" />
            <h4 className="text-lg font-medium text-gray-900 mb-2">
              {useAdminOverride ? 'Processing Logoff' : 'Verifying Fingerprint'}
            </h4>
            <p className="text-sm text-gray-600 mb-2">
              {useAdminOverride
                ? 'Processing logoff with admin override...'
                : 'Please place your finger on the sensor and wait...'}
            </p>
            {statusMessage && (
              <p className="text-sm text-primary-600 font-medium mt-2">
                {statusMessage}
              </p>
            )}
          </div>
        )}

        {step === 'success' && (
          <div className="text-center py-8">
            <CheckCircle className="w-16 h-16 mx-auto text-green-500 mb-4" />
            <h4 className="text-lg font-medium text-gray-900 mb-2">Logoff Successful</h4>
            <p className="text-sm text-gray-600">
              The employee has been logged off successfully.
            </p>
          </div>
        )}

        {step === 'error' && (
          <div className="text-center py-8">
            <AlertCircle className="w-16 h-16 mx-auto text-red-500 mb-4" />
            <h4 className="text-lg font-medium text-gray-900 mb-2">Error</h4>
            <p className="text-sm text-red-600 mb-6">{errorMessage}</p>
            <div className="flex gap-4 justify-center">
              {canMarkAttendanceForOthers && !useAdminOverride && (
                <button
                  onClick={() => {
                    setUseAdminOverride(true);
                    setStep('verify');
                    setErrorMessage('');
                  }}
                  className="px-6 py-2 bg-yellow-600 text-white rounded hover:bg-yellow-700"
                >
                  Use Admin Override
                </button>
              )}
              <button
                onClick={() => {
                  setStep('verify');
                  setErrorMessage('');
                }}
                className="px-6 py-2 bg-gray-300 text-gray-800 rounded hover:bg-gray-400"
              >
                Try Again
              </button>
              <button
                onClick={onClose}
                className="px-6 py-2 bg-primary-600 text-white rounded hover:bg-primary-700"
              >
                Close
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AttendanceLogoffModal;

